<script setup>
import { computed } from 'vue'

const props = defineProps({
	variant: {
		type: String,
		default: 'muted',
		// `accent` is the brand-blue "on / running" state that is neither Live nor a warning.
		validator: (v) => ['success', 'muted', 'warning', 'accent', 'error'].includes(v),
	},
	pulse: {
		type: Boolean,
		default: false,
	},
	pill: {
		type: Boolean,
		default: false,
	},
	size: {
		type: String,
		default: 'md',
		validator: (v) => ['sm', 'md'].includes(v),
	},
})

const wrapClass = computed(() => {
	const base = 'inline-flex items-center gap-1.5 whitespace-nowrap'
	if (props.pill) {
		const pillColor = {
			success: 'rounded-full bg-success-transparent text-success text-tiny font-medium px-2 py-0.5',
			muted: 'rounded-full bg-surface-3 text-surface-7 text-tiny font-medium px-2 py-0.5',
			warning: 'rounded-full bg-surface-3 text-surface-8 text-tiny font-medium px-2 py-0.5',
			accent: 'rounded-full bg-main-transparent text-main-light text-tiny font-medium px-2 py-0.5',
			error: 'rounded-full bg-error-transparent text-error-light text-tiny font-medium px-2 py-0.5',
		}[props.variant]
		return `${base} ${pillColor}`
	}
	const text = {
		success: 'text-success',
		muted: 'text-surface-7',
		warning: 'text-surface-8',
		accent: 'text-main-light',
		error: 'text-error-light',
	}[props.variant]
	const size = props.size === 'sm' ? 'text-tiny' : 'text-sm'
	return `${base} ${size} ${text}`
})

const dotClass = computed(() => {
	const color = {
		success: 'bg-success',
		muted: 'bg-surface-6',
		warning: 'bg-gold',
		accent: 'bg-main-light',
		error: 'bg-error',
	}[props.variant]
	const size = props.pill || props.size === 'sm' ? 'size-1.5' : 'size-2'
	const pulse = props.pulse ? 'animate-pulse' : ''
	return `rounded-full ${size} ${color} ${pulse}`.trim()
})
</script>

<template>
	<span :class="wrapClass">
		<span :class="dotClass" />
		<slot />
	</span>
</template>
